<template>
  <div class="mini-tab-content">
    <div
      v-if="loading"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="primary"
      />
      <span>{{ $t('involvedPanel.diagnosticHypothesis.loading.spinner') }}</span>
    </div>

    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="CrosshairIcon"
            size="13"
            class="mr-25 text-primary"
          />
          {{ activeCount ? activeCount + ' ' + $t('involvedPanel.diagnosticHypothesis.toolbar.cidsLinkedLabel') : $t('involvedPanel.diagnosticHypothesis.toolbar.noCidsLinked') }}
        </span>
      </div>

      <!-- Busca typeahead -->
      <div
        v-if="!readonly"
        class="dx-search"
      >
        <!-- Campo isolado: a lupa e o spinner se posicionam em relação a ele, e não ao bloco inteiro (que cresce
             quando a lista de resultados abre). -->
        <div class="dx-search__field">
          <feather-icon
            icon="SearchIcon"
            size="15"
            class="dx-search__icon"
          />
          <input
            ref="searchInput"
            v-model="query"
            class="form-control dx-search__input"
            type="text"
            :placeholder="$t('involvedPanel.diagnosticHypothesis.search.placeholder')"
            autocomplete="off"
            @input="onSearchInput"
            @focus="onSearchInput"
            @keydown.esc="closeResults"
          >
          <b-spinner
            v-if="searching"
            small
            class="dx-search__spinner"
          />
        </div>

        <div
          v-if="showResults"
          class="dx-results"
        >
          <button
            v-for="cid in results"
            :key="cid.id"
            type="button"
            class="dx-result"
            :disabled="linkingId === cid.id || isLinked(cid.id)"
            @click="linkCid(cid)"
          >
            <span class="dx-result__code">{{ formatCode(cid.code) }}</span>
            <span class="dx-result__name">{{ cid.name }}</span>
            <span
              v-if="isLinked(cid.id)"
              class="dx-result__tag"
            >{{ $t('involvedPanel.diagnosticHypothesis.search.alreadyLinked') }}</span>
            <b-spinner
              v-else-if="linkingId === cid.id"
              small
            />
            <feather-icon
              v-else
              icon="PlusCircleIcon"
              size="15"
              class="dx-result__add"
            />
          </button>

          <div
            v-if="!results.length && !searching"
            class="dx-results__empty"
          >
            {{ $t('involvedPanel.diagnosticHypothesis.search.noResults', { query }) }}
          </div>
        </div>
      </div>

      <!-- Lista de CIDs vinculados -->
      <div
        v-if="!hypotheses.length"
        class="interc-empty"
      >
        <feather-icon
          icon="CrosshairIcon"
          size="26"
        />
        <p>{{ $t('involvedPanel.diagnosticHypothesis.empty.noDiagnosticHypotheses') }}</p>
      </div>

      <div
        v-else
        class="dx-list"
      >
        <div
          v-for="h in hypotheses"
          :key="h.id"
          :class="['dx-item', { 'dx-item--cancelled': h.cancelled_by_user_id }]"
        >
          <span class="dx-item__code">{{ h.cid ? formatCode(h.cid.code) : '—' }}</span>
          <div class="dx-item__body">
            <span class="dx-item__name">{{ h.cid ? h.cid.name : $t('involvedPanel.diagnosticHypothesis.list.cidRemoved') }}</span>
            <div class="interc-item__meta">
              <feather-icon
                icon="UserIcon"
                size="11"
              />
              <span>{{ h.user ? h.user.name : '—' }}</span>
              <span class="interc-item__dot" />
              <feather-icon
                icon="ClockIcon"
                size="11"
              />
              <span>{{ h.created_at | formatDate }}</span>
              <template v-if="h.cancelled_by_user_id">
                <span class="interc-item__dot" />
                <span class="dx-item__cancelled-tag">
                  {{ $t('involvedPanel.diagnosticHypothesis.list.cancelledBy', { name: h.cancelled_by ? h.cancelled_by.name : '—' }) }}
                </span>
              </template>
            </div>
          </div>
          <button
            v-if="!readonly && !h.cancelled_by_user_id"
            class="interc-item__del"
            :title="$t('involvedPanel.diagnosticHypothesis.removeButton.title')"
            @click="cancelHypothesis(h.id)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="13"
            />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'DiagnosticHypothesisPanel',
  mixins: [involvedPanelMixin],
  data() {
    return {
      hypotheses: [],
      loading: false,
      query: '',
      results: [],
      searching: false,
      showResults: false,
      searchTimer: null,
      linkingId: null,
    }
  },
  computed: {
    activeCount() {
      return this.hypotheses.filter(h => !h.cancelled_by_user_id).length
    },
    // Ids de CID já vinculados ativamente (para desabilitar no dropdown).
    linkedCidIds() {
      return this.hypotheses
        .filter(h => !h.cancelled_by_user_id && h.cid)
        .map(h => h.cid.id)
    },
  },
  mounted() {
    this.fetchHypotheses()
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    // Chamado pelo mixin ao reativar a aba (keep-alive): evita dados velhos sem F5.
    refresh() {
      return this.fetchHypotheses()
    },

    // CID-10: subcategoria (4 chars) exibida com ponto (A000 -> A00.0).
    formatCode(code) {
      if (!code) return '—'
      return code.length > 3 ? `${code.slice(0, 3)}.${code.slice(3)}` : code
    },
    isLinked(cidId) {
      return this.linkedCidIds.includes(cidId)
    },
    closeResults() {
      this.showResults = false
    },
    onSearchInput() {
      const q = (this.query || '').trim()
      if (this.searchTimer) clearTimeout(this.searchTimer)
      if (q.length < 2) {
        this.results = []
        this.showResults = false
        return
      }
      this.searchTimer = setTimeout(() => this.runSearch(q), 300)
    },
    async runSearch(q) {
      this.searching = true
      this.showResults = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}cids/search`, { params: { q } })
        this.results = res.data.data || []
      } catch (e) {
        this.results = []
      } finally {
        this.searching = false
      }
    },
    async fetchHypotheses() {
      this.loading = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_diagnostic_hypotheses`, {
          params: {
            occurrence_id: Number(this.occurrenceId),
            involved_id: this.involvedId ? Number(this.involvedId) : undefined,
          },
        })
        this.hypotheses = res.data.data || []
      } catch (e) {
        this.hypotheses = []
        this.$message('Erro', this.$t('involvedPanel.diagnosticHypothesis.messages.loadHypothesesError'), 'error')
      } finally {
        this.loading = false
      }
    },
    async linkCid(cid) {
      if (this.isLinked(cid.id) || this.linkingId) return
      this.linkingId = cid.id
      try {
        await this.$http.post(`${this.$store.state.api}occurrence_diagnostic_hypotheses`, {
          occurrence_id: Number(this.occurrenceId),
          involved_id: this.involvedId ? Number(this.involvedId) : null,
          cid_id: cid.id,
        })
        // A resposta do POST não traz o autor (relação `user`); recarrega a lista.
        await this.refresh()
        this.query = ''
        this.results = []
        this.showResults = false
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : this.$t('involvedPanel.diagnosticHypothesis.messages.linkCidError')
        this.$message('Erro', msg, 'error')
      } finally {
        this.linkingId = null
      }
    },
    async cancelHypothesis(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.diagnosticHypothesis.messages.cancelHypothesisConfirm'),
        {
          title: this.$t('involvedPanel.diagnosticHypothesis.messages.cancelHypothesisTitle'), okTitle: this.$t('involvedPanel.diagnosticHypothesis.messages.cancelHypothesisOk'), cancelTitle: this.$t('involvedPanel.diagnosticHypothesis.messages.cancelHypothesisCancel'), okVariant: 'danger', centered: true, size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_diagnostic_hypotheses/${id}`)
        const updated = res.data.data || res.data
        const idx = this.hypotheses.findIndex(h => h.id === id)
        if (idx !== -1) this.$set(this.hypotheses, idx, updated)
      } catch (e) {
        this.$message('Erro', this.$t('involvedPanel.diagnosticHypothesis.messages.cancelHypothesisError'), 'error')
      }
    },
  },
}
</script>

<style scoped>
/* ── Busca ───────────────────────────────────────────────────── */
.dx-search {
  margin-bottom: 14px;
}

.dx-search__field {
  position: relative;
}

.dx-search__icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #b9b9c3;
  pointer-events: none;
}

.dx-search__input {
  padding-left: 32px;
  font-size: 13px;
}

.dx-search__spinner {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

/* No fluxo da página (não absoluta): o cartão do envolvido e a aba cortam o que sai delas (overflow:hidden), então
   uma lista por cima do conteúdo ficava pela metade. Em fluxo, ela empurra o que vem abaixo e nunca é cortada. */
.dx-results {
  margin-top: 4px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(34, 41, 47, 0.12);
}

.dx-result {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: none;
  border-bottom: 1px solid #f3f2f7;
  background: #fff;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.12s;
}

.dx-result:last-child {
  border-bottom: none;
}

.dx-result:hover:not(:disabled) {
  background: #f8f7ff;
}

.dx-result:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.dx-result__code {
  flex-shrink: 0;
  font-weight: 800;
  font-size: 12px;
  color: #7367f0;
  background: #f0eefe;
  border-radius: 5px;
  padding: 2px 7px;
  min-width: 56px;
  text-align: center;
}

.dx-result__name {
  flex: 1;
  font-size: 12.5px;
  color: #5e5873;
  line-height: 1.3;
}

.dx-result__add {
  flex-shrink: 0;
  color: #28c76f;
}

.dx-result__tag {
  flex-shrink: 0;
  font-size: 10px;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.dx-results__empty {
  padding: 14px 12px;
  font-size: 12px;
  color: #b9b9c3;
  text-align: center;
}

/* ── Lista de vinculados ─────────────────────────────────────── */
.dx-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.dx-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid #ebe9f1;
  border-left: 3px solid #7367f0;
  border-radius: 0 8px 8px 0;
  padding: 9px 12px;
  background: #fff;
}

.dx-item--cancelled {
  border-left-color: #b0b0b0;
  background: rgba(0, 0, 0, 0.03);
  opacity: 0.7;
}

.dx-item__code {
  flex-shrink: 0;
  font-weight: 800;
  font-size: 12px;
  color: #7367f0;
  background: #f0eefe;
  border-radius: 5px;
  padding: 3px 8px;
  min-width: 58px;
  text-align: center;
}

.dx-item--cancelled .dx-item__code {
  color: #6e6b7b;
  background: rgba(0, 0, 0, 0.07);
  text-decoration: line-through;
}

.dx-item__body {
  flex: 1;
  min-width: 0;
}

.dx-item__name {
  display: block;
  font-size: 13px;
  color: #5e5873;
  line-height: 1.35;
  margin-bottom: 3px;
}

.dx-item--cancelled .dx-item__name {
  text-decoration: line-through;
  color: #b9b9c3;
}

.dx-item__cancelled-tag {
  color: #ea5455;
}

/* ── Dark layout ─────────────────────────────────────────────── */
.dark-layout .dx-results {
  background: #283046;
  border-color: #3b4253;
}
.dark-layout .dx-result,
.dark-layout .dx-result:last-child {
  background: #283046;
  border-color: #3b4253;
}
.dark-layout .dx-result:hover:not(:disabled) {
  background: #2f3859;
}
.dark-layout .dx-result__name,
.dark-layout .dx-item__name {
  color: #d0d2d6;
}
.dark-layout .dx-result__code,
.dark-layout .dx-item__code {
  background: #161d31;
}
.dark-layout .dx-item {
  background: #283046;
  border-color: #3b4253;
  border-left-color: #7367f0;
}
.dark-layout .dx-item--cancelled {
  background: rgba(0, 0, 0, 0.18);
}
</style>
